/* eslint-disable react-hooks/exhaustive-deps */
import { useTranslation } from 'react-i18next';
import { useState, useEffect, useMemo } from 'react';
import { useQuery } from '@apollo/client';
import CloseIcon from '@mui/icons-material/Close';
import { useLocalization } from '../../../../util/useLocalization';
import { FETCH_MODEL_PORTFOLIO_AUDIT_LOG, FETCH_THEME_AUDIT_LOG } from '../../../../components/tables/auditLog';
import { usePermissions } from '../../../../providers/userContextProvider';
import { usePageState } from '../../../../util/usePageState';
import {
  Box, Skeleton, Typography, Grid,
} from '../../../1-primative';
import {
  Table, TableHeadCell, TableRow, TableCell,
  TableBody, Pagination, Card, CardContent, IconButton,
  Dialog, DialogTitle, Badge,
} from '../../../2-component';
import { ModelPortfolioData } from '../../../4-module/workflowCompletion/subSteps';

interface LogItem {
  id: string;
  type: string;
  initiator: string;
  organizationUser: OrganizationUser | null; // Or a more specific type if it can be something other than null
  apiToken: ApiToken | null; // Or a more specific type if it can be something other than null
  objectType: string;
  changes: Changes;
  timestamp: string;
  traceId: string;
}

interface ApiToken {
  id: string;
  name: string;
  organizationUser: OrganizationUser; // Or a more specific type if it can be something other than null
}

interface OrganizationUser {
  firstName: string;
  lastName: string;
}

interface Changes {
  [key: string]: string[];
}

const ModelPortfolioAuditLogs = ({
  model,
  loading,
}: {
  model: ModelPortfolioData,
  loading: boolean,
}) => {
  const { t } = useTranslation(['client', 'household', 'modelPortfolios']);
  const { localizedDateTime } = useLocalization();
  const { permissions } = usePermissions();

  // Pagination
  const [pageStr, setPageStr] = usePageState<string>('1', 'page');
  const page = parseInt(pageStr, 10);
  const setPage = (newPage: number) => setPageStr(newPage.toString());

  const pageSize = 30;

  // Sorting
  const defaultSortField = 'timestamp';
  const [sortField, setSortField] = usePageState<string>(
    defaultSortField,
    'sf',
  );
  const [sortDescStr, setSortDescStr] = usePageState<string>('true', 'sd');
  const sortDesc = sortDescStr === 'true';
  const setSortDesc = (value: boolean) => setSortDescStr(value.toString());

  const [logs, setLogs] = useState<{ logs: LogItem[], totalCount: number }>();
  const [activeLog, setActiveLog] = useState<LogItem | null>(null);
  const [open, setOpen] = useState(false);

  const {
    loading: loadingModelPortfolioLogs, data: modelPortfolioLogsData,
    refetch: refetchModelPortfolioLogs,
  } = useQuery(FETCH_MODEL_PORTFOLIO_AUDIT_LOG(permissions), {
    fetchPolicy: 'no-cache',
    errorPolicy: 'all',
    skip: !model,
    variables: {
      modelPortfolioId: model?.id,
      input: {
        filter: { },
        pagination: {
          perPage: pageSize / 2,
          sortField,
          sortDesc,
          offSet: (page - 1) * pageSize / 2,
        },
      },
    },
  });

  const {
    loading: loadingThemeLogs, data: themeLogsData, refetch: refetchThemeLogs,
  } = useQuery(FETCH_THEME_AUDIT_LOG(permissions), {
    fetchPolicy: 'no-cache',
    errorPolicy: 'all',
    skip: !model,
    variables: {
      themeId: model?.theme?.id,
      input: {
        filter: { },
        pagination: {
          perPage: pageSize / 2,
          sortField,
          sortDesc,
          offSet: (page - 1) * pageSize / 2,
        },
      },
    },
  });

  useMemo(() => {
    if (!loading && model) {
      // Combine logs from both queries
      const modelPortfolioLogs = modelPortfolioLogsData?.fetchModelPortfolio?.modelPortfolio?.modelPortfolioLogs || [];
      const themeLogs = themeLogsData?.fetchTheme?.theme?.logs || [];

      const updatedLogs = {
        logs: modelPortfolioLogs?.logs?.concat(themeLogs?.logs),
        totalCount: (modelPortfolioLogs.totalCount ?? 0) + (themeLogs?.totalCount ?? 0),
      };
      setLogs(updatedLogs);
    }
  }, [modelPortfolioLogsData, themeLogsData]);

  useEffect(() => {
    if (!loading && model) {
      refetchModelPortfolioLogs();
      refetchThemeLogs();
    }
  }, [loading, model, refetchModelPortfolioLogs, refetchThemeLogs]);

  // Add useEffect to reset page when filters or sorting changes
  useEffect(() => {
    setPage(1);
  }, [sortField, sortDesc]);

  const onSort = (field: string) => {
    setSortDesc(!sortDesc);
    setSortField(field);
    setPage(1); // Reset to first page when sorting changes
  };

  const sortDirection = (field: string) => (sortField === field ? (sortDesc ? 'desc' : 'asc') : undefined);

  const changedBy = (log: LogItem) => {
    if (log?.initiator === 'ORGANIZATION_USER') {
      return `${log?.organizationUser?.firstName} ${log?.organizationUser?.lastName}`;
    }
    if (log?.initiator === 'API_TOKEN' && log?.apiToken?.name) {
      const tokenCreator = log?.apiToken.organizationUser;
      return `${log?.apiToken?.name}${tokenCreator ? ` (${tokenCreator.firstName} ${tokenCreator.lastName})` : ''}`;
    }

    return log?.initiator;
  };

  const changeObjectType = (log: LogItem) => {
    let changedObjectType = log?.objectType;
    if (log?.objectType === 'ModelPortfolio') {
      changedObjectType = 'Model';
    }
    return changedObjectType;
  };

  const changedText = (log: LogItem) => {
    const keys = Object.keys(log?.changes || {});

    if (keys.length === 1) {
      return keys[0];
    }

    if (keys.length === 2) {
      return `${keys[0]} & ${keys[1]}`;
    }

    return `${keys[0]}, ${keys[1]} & ${keys.length - 2} ${t('moreFields')}`;
  };

  if (loading || loadingModelPortfolioLogs || loadingThemeLogs) {
    return (
      <>
        <Card>
          <CardContent>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Skeleton width="100%" height="56px" />
              </Grid>
              <Grid item xs={12}>
                <Skeleton width="100%" height="112px" />
              </Grid>
              <Grid item xs={12}>
                <Skeleton width="100%" height="56px" />
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </>
    );
  }

  return (
    <>
      <Card loading={loading} data-testid="model-portfolios-card" sx={{
        display: 'flex',
        flexDirection: 'column',
        maxHeight: 'calc(100vh - 300px)',
        mb: 2,
      }}>
        <Box sx={{
          display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden',
        }}>
          <Box sx={{
            display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0,
          }}>
            <Box sx={{ overflowX: 'auto', overflowY: 'auto', flex: 1 }}>
              <Table sx={{ minWidth: 650 }} aria-label="table" data-testid="model-portfolios-table">
                <TableBody>
                  <TableRow>
                    <TableHeadCell
                      width="20%"
                      testId="model-portfolios-table-changed-by"
                    >
                      <Typography variant='labelSmall' sx={{ fontSize: '12px' }}>{t('audit.changedBy')}</Typography>
                    </TableHeadCell>
                    <TableHeadCell
                      width="10%"
                      testId="model-portfolios-table-type"
                    >
                      <Typography variant='labelSmall' sx={{ fontSize: '12px' }}>{t('audit.type')}</Typography>
                    </TableHeadCell>
                    <TableHeadCell
                      width="10%"
                      testId="model-portfolios-table-changed-object"
                    >
                      <Typography variant='labelSmall' sx={{ fontSize: '12px' }}>{t('audit.changedObject')}</Typography>
                    </TableHeadCell>
                    <TableHeadCell
                      width="40%"
                      testId="model-portfolios-table-changed-fields"
                    >
                      <Typography variant='labelSmall' sx={{ fontSize: '12px' }}>{t('audit.changedFields')}</Typography>
                    </TableHeadCell>
                    <TableHeadCell
                      width="20%"
                      testId="model-portfolios-table-timestamp"
                      isSortable
                      onClick={() => onSort('timestamp')}
                      sortDirection={sortDirection('timestamp')}
                    >
                      <Typography variant='labelSmall' sx={{ fontSize: '12px' }}>{t('audit.timestamp')}</Typography>
                    </TableHeadCell>
                  </TableRow>
                  {loading && [...Array(pageSize)].map((_, index) => (
                    <TableRow key={index}>
                      <TableCell testId="model-portfolios-table-changed-by" width="20%"><Skeleton width='100%' /></TableCell>
                      <TableCell testId="model-portfolios-table-type" width="10%"><Skeleton width='100%' /></TableCell>
                      <TableCell testId="model-portfolios-table-changed-object" width="10%"><Skeleton width='100%' /></TableCell>
                      <TableCell testId="model-portfolios-table-changed-fields" width="40%"><Skeleton width='100%' /></TableCell>
                      <TableCell testId="model-portfolios-table-timestamp" width="20%"><Skeleton width='100%' /></TableCell>
                    </TableRow>
                  ))}
                  {logs?.logs?.map((log: LogItem) => (
                    <TableRow
                      hover
                      key={log?.id}
                      sx={{ textDecoration: 'none', cursor: 'pointer' }}
                      onDoubleClick={(e) => {
                        setActiveLog(log);
                        setOpen(true);
                      }}
                    >
                      <TableCell testId="model-portfolios-table-changed-by" width="20%">
                        <Typography variant='bodyMedium' sx={{ fontSize: '14px' }}>
                          {changedBy(log)}
                        </Typography>
                      </TableCell>
                      <TableCell testId="model-portfolios-table-type" width="10%">
                        <Badge label={log?.type} color={log?.type === 'DELETE' ? 'negative' : log?.type === 'CREATE' ? 'positive' : 'neutral'} />
                      </TableCell>
                      <TableCell testId="model-portfolios-table-changed-object" width="10%">
                        <Typography variant='bodyMedium' sx={{ fontSize: '14px' }}>
                          {changeObjectType(log)}
                        </Typography>
                      </TableCell>
                      <TableCell testId="model-portfolios-table-changed-fields" width="40%">
                        <Typography variant='bodyMedium' sx={{ fontSize: '14px' }}>
                          {changedText(log)}
                        </Typography>
                      </TableCell>
                      <TableCell testId="model-portfolios-table-timestamp" width="20%">
                        <Typography variant='bodyMedium' sx={{ fontSize: '14px' }}>
                          {localizedDateTime(log?.timestamp)}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
            <Box sx={{
              backgroundColor: 'background.paper',
              borderTop: '1px solid',
              borderColor: 'divider',
            }}>
              <Box sx={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 1,
              }}>
                <Typography variant="button" data-testid="model-portfolios-table-total-count">
                  {t('modelPortfolios:modelPortfolioTable.totalCount')}: {logs?.totalCount}
                </Typography>
                <Pagination
                  count={Math.ceil((logs?.totalCount || 0) / pageSize)}
                  page={page}
                  perPage={pageSize}
                  onChange={(_e, newPage) => setPage(newPage)}
                  sx={{
                    p: 1,
                    textAlign: 'right',
                    '.MuiPagination-ul': {
                      justifyContent: 'end',
                    },
                  }}
                />
              </Box>
            </Box>
          </Box>
        </Box>
      </Card>
      {activeLog && (
        <Dialog open={open} onClose={() => setOpen(false)} maxWidth='md' fullWidth>
          <DialogTitle>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }} alignItems='center'>
              {activeLog.objectType} - {localizedDateTime(activeLog.timestamp)}
              <Box display='flex' justifyContent='end' alignItems='center'>
                <Box ml={1} />
                <IconButton onClick={() => setOpen(false)} data-testid="model-portfolios-table-close-button">
                  <CloseIcon />
                </IconButton>
              </Box>
            </Box>
          </DialogTitle>
          <Table sx={{ display: 'table', overflowX: 'scroll' }} aria-label="table">
            <TableBody>
              <TableRow>
                <TableHeadCell testId="model-portfolios-table-field">{t('audit.field')}</TableHeadCell>
                <TableHeadCell testId="model-portfolios-table-from">{t('audit.from')}</TableHeadCell>
                <TableHeadCell testId="model-portfolios-table-to">{t('audit.to')}</TableHeadCell>
              </TableRow>
              {Object.keys(activeLog.changes || {}).map((key: string) => (
                <TableRow
                  hover
                  key={key}
                >
                  <TableCell testId="model-portfolios-table-field">{key}</TableCell>
                  <TableCell testId="model-portfolios-table-from">{activeLog.changes[key][0] ? JSON.stringify(activeLog.changes[key][0]) : '-'}</TableCell>
                  <TableCell testId="model-portfolios-table-to">{activeLog.changes[key][1] ? JSON.stringify(activeLog.changes[key][1]) : '-'}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Dialog>
      )}
    </>
  );
};

export default ModelPortfolioAuditLogs;
